import { useState } from 'react';
import { Alert, Button, ButtonGroup, Stack, Typography } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdfOutlined';
import TableViewIcon from '@mui/icons-material/TableViewOutlined';
import {
  DepartureExportFormat,
  DepartureExportList,
  DeparturePaymentEligibleParams,
  downloadDepartureExport,
} from '../../api/readyToFly';

const LISTS: Array<{ list: DepartureExportList; label: string; hint: string }> = [
  { list: 'travel-agent', label: 'Travel Agent List', hint: 'Name and passport number' },
  {
    list: 'detailed',
    label: 'Detailed Student List',
    hint: 'Passport, contact, university, batch, visa and flight',
  },
];

/** Batches to Departures exports (Phase 94). Uses the table's current
 * filters; the server applies the caller's own branch scope, so a file
 * never contains anyone the table wouldn't show. */
export const DepartureExportButtons = ({
  filters,
}: {
  filters: Omit<DeparturePaymentEligibleParams, 'page' | 'pageSize'>;
}) => {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (list: DepartureExportList, format: DepartureExportFormat) => {
    setBusy(`${list}:${format}`);
    setError(null);
    try {
      await downloadDepartureExport(list, format, filters);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export the list');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
        {LISTS.map(({ list, label, hint }) => (
          <Stack key={list} spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              {label} — {hint}
            </Typography>
            <ButtonGroup
              size="small"
              variant="outlined"
              aria-label={`Export ${label}`}
              disabled={busy !== null}
            >
              <Button startIcon={<PictureAsPdfIcon />} onClick={() => run(list, 'pdf')}>
                {busy === `${list}:pdf` ? 'Preparing…' : 'PDF'}
              </Button>
              <Button startIcon={<TableViewIcon />} onClick={() => run(list, 'xlsx')}>
                {busy === `${list}:xlsx` ? 'Preparing…' : 'Excel'}
              </Button>
            </ButtonGroup>
          </Stack>
        ))}
      </Stack>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
    </Stack>
  );
};
